/* eslint-disable ts/naming-convention */
const XSRF_TOKEN_COOKIE = 'XSRF-TOKEN'
const CSRF_COOKIE_URL = 'sanctum/csrf-cookie'
const APPLICATION_JSON = 'application/json'
const STATUS_TOKEN_MISMATCH = 419
const STATUS_UNAUTHORIZED = 401
const DEFAULT_ERROR_STATUS = 500
const LEADING_SLASHES_REGEX = /^\/+/
/* eslint-enable ts/naming-convention */

interface RequestOptions {
	headers?: Record<string, string>
	method?: string
	[key: string]: unknown
}

interface BaseParameters {
	baseURL: string
	headers: Record<string, string>
	credentials?: RequestCredentials
	mode: RequestMode
	[key: string]: unknown
}

const getBaseParameters = (
	options: RequestOptions,
	lang: string,
	formData: boolean = false,
	isAI: boolean = false,
): BaseParameters => {
	const config = useNuxtApp().$config
	const xsrfToken = useCookie(XSRF_TOKEN_COOKIE)

	const headers: Record<string, string> = {
		accept: APPLICATION_JSON,
		'Accept-Language': lang,
		'X-Client-Type': EApiClientType.CRM,
		...options?.headers,
	}

	if (!formData) {
		headers['Content-Type'] = APPLICATION_JSON
	}

	if (xsrfToken?.value) {
		headers['X-XSRF-TOKEN'] = xsrfToken.value
	}

	return {
		baseURL: isAI ? config.public.apiAIBaseURL : config.public.apiBaseURL,
		headers,
		...(isAI ? {} : { credentials: 'include' as RequestCredentials }),
		mode: 'cors' as RequestMode,
		...options,
	}
}

const isCookieRequest = (request: string): boolean => request === CSRF_COOKIE_URL
const AUTH_EXCLUDED_REQUESTS = [CSRF_COOKIE_URL, 'login', 'register', 'logout']

const isUnauthorizedHandlingEnabled = (request: string): boolean => {
	const normalizedRequest = request.replace(LEADING_SLASHES_REGEX, '')
	return !AUTH_EXCLUDED_REQUESTS.some(
		excluded => normalizedRequest === excluded || normalizedRequest.endsWith(`/${excluded}`),
	)
}

const handleUnauthorizedRequest = async (request: string): Promise<void> => {
	if (!isUnauthorizedHandlingEnabled(request)) return

	const isLogged = useCookie('is_logged')
	isLogged.value = 'false'

	await navigateTo('/login?sessionExpired=true')
}

const handleCookieRequest = async (request: string, options: RequestOptions): Promise<void> => {
	if (!isCookieRequest(request) && options?.method !== 'GET') {
		const xsrfToken = useCookie(XSRF_TOKEN_COOKIE)
		if (!xsrfToken?.value) {
			await ApiService.setCookies()
		}
	}
}

export const apiCommon = async <T>(
	request: string,
	options: RequestOptions,
	lang = 'pl',
	formData: boolean = false,
	isAI: boolean = false,
): Promise<IApiCommonModel<T>> => {
	const setParameters = (): BaseParameters => getBaseParameters(options, lang, formData, isAI)

	const fetchData = async (): Promise<IApiCommonModel<T>> => {
		try {
			const response = await $fetch<IApiCommon<T>>(`/${request}`, setParameters())

			if (!response) {
				return response as IApiCommonModel<T>
			}

			return response?.meta
				? ({ ...response, meta: ApiMeta(response.meta) } as unknown as IApiCommonModel<T>)
				: (response as unknown as IApiCommonModel<T>)
		} catch (error: unknown) {
			if (isAbortError(error)) throw error

			const errorData = {
				...error?.data,
				status: error?.response?.status ?? error?.status ?? DEFAULT_ERROR_STATUS,
			} satisfies IError

			const apiError = new Error(errorData.message ?? 'API Error')
			Object.assign(apiError, errorData)

			throw apiError
		}
	}

	try {
		await handleCookieRequest(request, options)
		return await fetchData()
	} catch (error) {
		const typedError = error as IError

		if (isAbortError(error)) throw error

		if (typedError.status === STATUS_TOKEN_MISMATCH && !isCookieRequest(request)) {
			await ApiService.setCookies()
			return fetchData()
		}
		if (typedError.status === STATUS_UNAUTHORIZED) {
			await handleUnauthorizedRequest(request)
		}

		throw error
	}
}

export const apiCommonDirect = async <T>(
	request: string,
	options: RequestOptions,
	lang = 'pl',
	formData: boolean = false,
	isAI: boolean = false,
): Promise<T> => {
	const response = await apiCommon(request, options, lang, formData, isAI)
	return response as unknown as T
}

export const apiCommonRaw = async <T>(
	request: string,
	options: RequestOptions,
	lang = 'pl',
	formData: boolean = false,
): Promise<IApiCommonRawModel<T>> => {
	const setParameters = (): BaseParameters => getBaseParameters(options, lang, formData)

	const fetchData = async (): Promise<IApiCommonRawModel<T>> => {
		try {
			const response = await $fetch.raw<T>(`/${request}`, setParameters())
			return response as IApiCommonRawModel<T>
		} catch (error) {
			console.error('Error in apiCommonRaw:', error)
			throw error
		}
	}

	try {
		await handleCookieRequest(request, options)
		return await fetchData()
	} catch (error) {
		const typedError = error as IError
		if (typedError.status === STATUS_TOKEN_MISMATCH && !isCookieRequest(request)) {
			await ApiService.setCookies()
			return fetchData()
		}
		throw error
	}
}
